<template>
	<div
		v-if="activeSpec"
		class="jw-pro-pricing-exp"
		:class="{
			'jw-pro-pricing-exp--boxed': activeSpec.showPlanSelector,
			'jw-pro-pricing-exp--spaced': !activeSpec.showPlanSelector,
			'jw-pro-pricing-exp--triple-plan': activeSpec.showLifetimeOption && activeSpec.showYearlyOption,
		}"
	>
		<div class="jw-pro-pricing-exp__header">
			<img
				v-if="activeSpec.logoOverride"
				class="jw-pro-pricing-exp__logo-override"
				:src="activeSpec.logoOverride"
				alt="JustWatch TV Plus"
			/>
			<template v-else>
				<div class="jw-pro-pricing-exp__badge-row">
					<img class="jw-pro-pricing-exp__logo" :src="justWatchLogo" alt="JustWatch Logo" />
					<span class="freemium-filter__badge freemium-filter__badge--sm">
						<FontAwesomeIcon :icon="jwCrownIcon" size="sm" />
						{{ activeSpec.badgeText ?? $t('WEBAPP_FREEMIUM_TAG') }}
					</span>
				</div>

				<span v-if="activeSpec.tagline" class="jw-pro-pricing-exp__tagline">{{ activeSpec.tagline }}</span>
			</template>

			<h2 class="jw-pro-pricing-exp__title">{{ $t(activeSpec.titleKey) }}</h2>
		</div>

		<div class="jw-pro-pricing-exp__perks">
			<div v-for="perk in activeSpec.perks" :key="perk.textKey" class="jw-pro-pricing-exp__perk">
				<FontAwesomeIcon v-if="perk.faIcon" class="jw-pro-pricing-exp__perk-icon" :icon="perk.faIcon" />
				<img v-else class="jw-pro-pricing-exp__perk-icon" :src="perk.icon" alt="" />
				<div class="jw-pro-pricing-exp__perk-description">
					<h3>{{ $t(perk.titleKey, perk.titleParams) }}</h3>
					<span class="jw-pro-pricing-exp__perk-note">{{ $t(perk.textKey) }}{{ perk.textSuffix }}</span>
				</div>
			</div>
		</div>

		<div v-if="!activeSpec.showPlanSelector" class="jw-pro-pricing-exp__signup">
			<div class="jw-pro-pricing-exp__price-info">
				<i18n-t keypath="WEBAPP_PRO_MODAL_JUST_PER_MONTH" tag="h3" class="jw-pro-pricing-exp__price">
					<template #price>
						<span v-if="activeSpec.showOriginalPrice" class="jw-pro-pricing-exp__price--original"
							><s>{{ formattedOriginalPrice }}</s></span
						>
						<span class="jw-pro-pricing-exp__price--discounted">{{ formattedDiscountedMonthlyPrice }}</span>
					</template>
				</i18n-t>
				<p class="jw-pro-pricing-exp__cancel-note">{{ $t('WEBAPP_FREEMIUM_MODAL_CANCEL') }}</p>
			</div>

			<BasicButton color="primary" size="medium" class="jw-pro-pricing-exp__cta" @click="submit">{{
				$t('WEBAPP_GET_PROVIDER_X', { provider: activeSpec.ctaProvider ?? 'JustWatch Pro' })
			}}</BasicButton>
		</div>

		<div v-else class="jw-pro-pricing-exp__plan-selector">
			<div class="jw-pro-pricing-exp__plan-cards">
				<button
					v-for="plan in planOptions"
					:key="plan.key"
					type="button"
					class="jw-pro-pricing-exp__plan-card"
					:class="{
						'jw-pro-pricing-exp__plan-card--selected': selectedPlan === plan.key,
						'jw-pro-pricing-exp__plan-card--lifetime':
							plan.key === 'lifetime' && activeSpec.showLifetimeOption && activeSpec.showYearlyOption,
					}"
					@click="selectPlan(plan.key)"
				>
					<span class="jw-pro-pricing-exp__plan-card-name">{{ plan.name }}</span>
					<span class="jw-pro-pricing-exp__plan-card-price-unit">
						<i18n-t v-if="plan.priceUnitKey" :keypath="plan.priceUnitKey" tag="span">
							<template #price>
								<span class="jw-pro-pricing-exp__plan-card-price">{{ plan.priceValue }}</span>
							</template>
						</i18n-t>
						<span v-else class="jw-pro-pricing-exp__plan-card-price">{{ plan.priceValue }}</span>
					</span>
				</button>
			</div>

			<p class="jw-pro-pricing-exp__cancel-note">{{ $t('WEBAPP_FREEMIUM_MODAL_CANCEL') }}</p>

			<BasicButton color="primary" :size="ctaSize" blocked class="jw-pro-pricing-exp__cta" @click="submit">{{
				$t('WEBAPP_GET_PROVIDER_X', { provider: activeSpec.ctaProvider ?? 'JustWatch Pro' })
			}}</BasicButton>
		</div>

		<div class="jw-pro-pricing-exp__footer">
			<i18n-t keypath="WEBAPP_JUSTWATCH_PRO_LEGAL" tag="p" class="jw-pro-pricing-exp__legal-note">
				<template #terms>
					<a :href="`${webLocale}/terms`">{{ $t('WEBAPP_JUSTWATCH_PRO_LEGAL_TERMS') }}</a>
				</template>

				<template #privacy>
					<a :href="`${webLocale}/privacy-policy`">{{ $t('WEBAPP_JUSTWATCH_PRO_LEGAL_PRIVACY') }}</a>
				</template>
			</i18n-t>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';

import BasicButton from '@/ui-components/BasicButton.vue';
import { jwCrownIcon } from '@/plugins/global-fontawesome';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { getLastProView, priceFormatter } from '@/helpers/pro-web-helper';
import { getViewport } from '@/helpers/ui-helper';
import { useUserAgent } from '@/helpers/composables/useUserAgent';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { UserAccountEvents } from '@/enums/events';
import { Viewport } from '@/enums/viewport';
import type { PriceInfo, SubscriptionTitles } from '@/features/jw-pro/types';
import { SUBSCRIPTIONS } from '@/features/jw-pro/constants';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { useExperiment } from '@/libraries/experiment';
import { PlanKey, VARIANT_SPECS, getTrackingDeviceValue } from '@/components/experiments/JwProPricingExp/helper';

const props = defineProps<{
	additionalContexts?: SnowplowContext[];
}>();

const emit = defineEmits<{ (e: 'submit', priceId: string): void }>();

const { webLocale, locale, country } = storeToRefs(useLanguageStore());
const { t } = useI18n();

// Activation happens in ProModal.vue (the outer shell); this reads the default 'local' status
// as a descendant, matching this codebase's experiment-activation pattern.
const { activeVariantWithControlGroup: variant, trackEvent } = useExperiment('EXP_JW_PRO_PRICING');

const justWatchLogo = `/${ASSETS_DIR}/img/logo/JustWatch-logo.png`;

const viewport = computed(() => getViewport());
const ctaSize = computed(() => (viewport.value === Viewport.SMARTPHONE ? 'medium' : 'large'));

const activeSpec = computed(() => VARIANT_SPECS[variant.value ?? '']);

// "original" is the Control group's price (struck through on variant_1), the monthly/yearly/lifetime
// prices below are each variant's own real SUBSCRIPTIONS price.
function getSubscriptionPrice(subscriptionKey: SubscriptionTitles, country: string): PriceInfo {
	const { prices } = SUBSCRIPTIONS[subscriptionKey];

	return (country in prices ? prices[country] : prices.DEFAULT) as PriceInfo;
}

// Yearly/lifetime aren't offered by every variant, so these keys may genuinely be absent.
function getOptionalSubscriptionPrice(
	subscriptionKey: SubscriptionTitles | undefined,
	country: string
): PriceInfo | undefined {
	if (!subscriptionKey) return undefined;

	return getSubscriptionPrice(subscriptionKey, country);
}

const originalPriceInfo = computed(() => getSubscriptionPrice('proPricingExpV2Control', country.value));
const discountedPriceInfo = computed(() =>
	getSubscriptionPrice(activeSpec.value?.monthlyPriceKey ?? 'proPricingExpV2Variant1Monthly', country.value)
);
const yearlyPriceInfo = computed(() => getOptionalSubscriptionPrice(activeSpec.value?.yearlyPriceKey, country.value));
const lifetimePriceInfo = computed(() =>
	getOptionalSubscriptionPrice(activeSpec.value?.lifetimePriceKey, country.value)
);

const formattedOriginalPrice = computed(() =>
	priceFormatter(locale.value, originalPriceInfo.value.price, originalPriceInfo.value.currency)
);

const formattedDiscountedMonthlyPrice = computed(() =>
	priceFormatter(locale.value, discountedPriceInfo.value.price, discountedPriceInfo.value.currency)
);

// variant_3/4/5: monthly + yearly/lifetime plan selector — every price shown (and every priceId
// submitted at checkout) is the real SUBSCRIPTIONS price for that plan.
// priceUnitKey is undefined for lifetime — it has no "/ month" or "/ year" suffix to style separately
type PlanOption = {
	key: PlanKey;
	name: string;
	priceValue: string;
	priceUnitKey?: string;
};

const userSelectedPlan = ref<PlanKey | null>(null);
const defaultPlanKey = computed<PlanKey>(() => activeSpec.value?.defaultPlanKey ?? 'monthly');
const selectedPlan = computed<PlanKey>(() => userSelectedPlan.value ?? defaultPlanKey.value);

const { deviceType } = useUserAgent();
const trackingDeviceValue = computed(() => getTrackingDeviceValue(deviceType.value));

function selectPlan(planKey: PlanKey) {
	userSelectedPlan.value = planKey;
	trackEvent({ action: 'click', label: `select_${planKey}`, value: trackingDeviceValue.value });
}

const planOptions = computed<PlanOption[]>(() => {
	const spec = activeSpec.value;
	if (!spec?.showPlanSelector) return [];

	const currency = discountedPriceInfo.value.currency;
	const formatPrice = (amount: number) => priceFormatter(locale.value, amount, currency);

	const options: PlanOption[] = [
		{
			key: 'monthly',
			name: t('WEBAPP_MONTHLY') as string,
			priceValue: formatPrice(discountedPriceInfo.value.price),
			priceUnitKey: 'WEBAPP_X_PER_MONTH',
		},
	];

	if (spec.showLifetimeOption) {
		options.push({
			key: 'lifetime',
			name: t('WEBAPP_LIFETIME') as string,
			priceValue: formatPrice(lifetimePriceInfo.value?.price ?? 0),
		});
	}

	if (spec.showYearlyOption) {
		const yearlyPrice = yearlyPriceInfo.value?.price ?? 0;
		options.push({
			key: 'yearly',
			name: t('WEBAPP_YEARLY') as string,
			priceValue: formatPrice(yearlyPrice),
			priceUnitKey: 'WEBAPP_X_PER_YEAR',
		});
	}

	return options;
});

// Non-selector variants never set defaultPlanKey and never render the plan-card click handler
// that could change userSelectedPlan, so selectedPlan is already 'monthly' for them.
const checkoutPriceId = computed(() => {
	if (selectedPlan.value === 'yearly') return yearlyPriceInfo.value?.priceId ?? discountedPriceInfo.value.priceId;
	if (selectedPlan.value === 'lifetime') return lifetimePriceInfo.value?.priceId ?? discountedPriceInfo.value.priceId;
	return discountedPriceInfo.value.priceId;
});

function submit() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: UserAccountEvents.GO_PRO_CLICKED,
			label: '',
			property: getLastProView() || undefined,
		},
		props.additionalContexts
	);

	trackEvent({ action: 'click', label: `clickout_${selectedPlan.value}`, value: trackingDeviceValue.value });

	emit('submit', checkoutPriceId.value);
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.jw-pro-pricing-exp {
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: 24px;

	// v3/4/5 (selectable/boxed pricing): fixed gap between sections
	&--boxed {
		gap: 16px;

		// pulls the header up a bit further so the legal note isn't flush against the bottom
		// edge of the (fixed-height) modal shell
		.jw-pro-pricing-exp__header {
			margin-top: -40px;
		}

		// anchors the legal footer to the bottom of the modal, matching v1/2/6's --spaced behavior
		// instead of letting it crowd right under the CTA when there's extra vertical room
		.jw-pro-pricing-exp__footer {
			margin-top: auto;
		}

		@media (max-width: 512px) {
			.jw-pro-pricing-exp__perks {
				margin-top: 18px;
			}

			// v3/v4: perks-to-plan-selector gap is 40px (16px base gap + 24px here) — v5 overrides
			// this back down below, in --triple-plan, to keep its own 34px spec
			.jw-pro-pricing-exp__plan-selector {
				margin-top: 24px;
			}
		}
	}

	// v1/2/6 (simple single-price signup): distribute space evenly between sections
	&--spaced {
		justify-content: space-around;
	}

	&--triple-plan {
		@media (max-width: 512px) {
			// perks-to-plan-selector gap is 34px (16px base gap + 18px here) for v5, vs 40px for v3/v4
			.jw-pro-pricing-exp__plan-selector {
				margin-top: 18px;
			}

			.jw-pro-pricing-exp__plan-card-name {
				font-size: 13px;
				font-weight: 700;
			}

			.jw-pro-pricing-exp__plan-card-price {
				font-size: 15px;
				font-weight: 700;
			}
		}
	}

	&__header {
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: -25px auto 0;
		text-align: center;
	}

	&__badge-row {
		display: flex;
		align-items: center;
	}

	&__logo {
		width: 150px;
		margin-inline-end: 5px;

		@media (max-width: 512px) {
			width: 125px;
		}
	}

	&__tagline {
		margin-top: 4px;
		color: $jw-color-gold;
		font-size: 13px;
		font-weight: 900;
		letter-spacing: 3px;
	}

	&__title {
		margin-top: 24px;
		margin-bottom: 0;
		font-size: 20px;
		font-weight: 900;

		@media (max-width: 512px) {
			font-size: 16px;
		}
	}

	&__perks {
		display: flex;
		flex-direction: column;
		border-radius: $jw-border-radius-large;
		background-color: $jw-color-gold;
		padding: 24px 32px;
		margin: 0 16px;

		@media (max-width: 512px) {
			padding: 16px 20px;
			margin: 0;
		}
	}

	&__perk {
		display: flex;
		align-items: center;
		margin: 15px 0;

		@media (max-width: 512px) {
			margin: 8px 0;
		}
	}

	&__perk-icon {
		width: 24px;
		height: 32px;
		object-fit: contain;
		// FontAwesomeIcon perks (faIcon) fill with currentColor — without this they inherit the
		// wrong color; image-based perks (icon) are unaffected since they're already black pixels.
		color: black;
	}

	&__perk-description {
		display: flex;
		flex-direction: column;
		gap: 8px;
		color: black;
		margin-inline-start: 20px;

		@media (max-width: 512px) {
			gap: 4px;
		}

		> h3 {
			margin: 0;
			font-size: 16px;
			font-style: normal;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.24px;
		}
	}

	&__perk-note {
		font-size: 14px;
		font-style: normal;
		color: #454a51;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.21px;
	}

	&__signup {
		display: flex;
		justify-content: space-between;
		margin: 0 16px;

		@media (max-height: 767px) {
			margin: 22px 0;
		}

		@media (max-width: 512px) {
			flex-direction: column;
			align-items: center;
			gap: 20px;

			> div > :is(h3, p) {
				text-align: center;
			}

			> button {
				width: 100%;
			}
		}
	}

	// BasicButton's own stylesheet is global (unscoped) and sets font-weight on the same root
	// element, so targeting .basic-button__content directly avoids a specificity tie with it.
	&__cta {
		:deep(.basic-button__content) {
			font-size: 16px;
			font-style: normal;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.24px;
			padding-top: 8px;
			padding-bottom: 8px;
		}
	}

	&__price-info {
		display: flex;
		flex-direction: column;
		justify-content: space-between;

		h3 {
			margin: 0;
		}
	}

	&__price--original {
		color: var(--text-text-medium, #d2d3d5);
		font-size: 20px;
		font-style: normal;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.3px;
		margin-inline-start: 4px;

		@media (max-height: 767px) {
			font-size: 15px;
		}
	}

	&__price--discounted {
		color: var(--text-text-high, #fff);
		font-size: 20px;
		font-style: normal;
		font-weight: 900;
		line-height: 24px;
		letter-spacing: 0.3px;
		margin-left: 4px;

		@media (max-height: 767px) {
			font-size: 15px;
		}
	}

	&__cancel-note {
		color: var(--text-text-low, #909398);
		font-size: 14px;
		font-style: normal;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.21px;
		margin-top: 8px;
		margin-bottom: 0;
	}

	&__plan-selector {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin: 0 16px;

		@media (max-width: 512px) {
			margin: 0;
		}

		.jw-pro-pricing-exp__cancel-note {
			text-align: center;

			// plan-cards-to-cancel-note gap is 24px (8px base gap + 16px here)
			@media (max-width: 512px) {
				margin-top: 16px;
			}
		}
	}

	&__plan-cards {
		display: flex;
		align-items: center;
		gap: 12px;

		@media (max-width: 512px) {
			flex-wrap: wrap;
		}
	}

	&__plan-card {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 3px;
		padding: 16px;
		border: 2px solid transparent;
		border-radius: 8px;
		background-color: $jw-color-primary-dark--lighter;
		color: var(--text-text-high, #fff);
		font: inherit;
		text-align: center;
		cursor: pointer;

		&:hover:not(&--selected) {
			background-color: rgba($jw-color-primary-dark--lighter, 0.8);
		}

		&--selected {
			padding: 24px 16px;
			border-color: $jw-color-gold;

			@media (max-width: 512px) {
				padding: 16px;
			}
		}

		// v5 only (the three-plan selector): Lifetime gets its own full-width row on mobile,
		// regardless of DOM position, so Monthly/Yearly can keep sharing a row above it. Its
		// own name/price still stack vertically like the other cards (the base column direction).
		&--lifetime {
			@media (max-width: 512px) {
				order: 1;
				flex: 1 1 100%;
			}
		}
	}

	&__plan-card-name {
		font-size: 16px;
		font-weight: 900;
	}

	&__plan-card-price {
		color: var(--text-text-high, #fff);
		font-size: 18px;
		font-weight: 900;
		line-height: 24px;

		@media (max-width: 512px) {
			font-size: 14px;
		}
	}

	// Wraps the price (monthly/yearly only) — the "/ month" or "/ year" suffix text takes this
	// muted style, while the nested .plan-card-price span keeps the bold price-number style
	&__plan-card-price-unit {
		font-size: 14px;
		font-style: normal;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.21px;
		color: var(--text-text-low, #909398);
	}

	&__footer {
		margin: 0 16px;
		text-align: center;

		@media (max-width: 512px) {
			margin: 0;
		}
	}

	&__legal-note {
		font-size: 12px;
		font-style: normal;
		font-weight: 400;
		line-height: 16px;
		letter-spacing: 0.18px;
		margin: 0;
	}
}
</style>
